import * as React from "react"
import "./map-snapshot.css"
import type { WebMercatorViewport } from "@deck.gl/core"
import { useEffect, useState, useRef, RefObject } from "react"
import { useHtmlId } from "@nova/use-html-id"
import { ButtonBar, ButtonPrimary, ButtonSecondary } from "@nova/component-btn"

const collectBoundingClientRect = <T extends SVGSVGElement>(r: RefObject<T | null>) => {
    if (!r.current || !r.current.parentElement) {
        return {
            width: 0,
            height: 0,
        }
    }
    return r.current.parentElement.getBoundingClientRect()
}

function useSVGViewport<T extends SVGSVGElement>(r: RefObject<T | null>) {
    const [viewport, setViewport] = useState(() => collectBoundingClientRect(r))
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | undefined = undefined
        setViewport(collectBoundingClientRect(r))

        const resizeEventHandler = () => {
            clearTimeout(timeout)
            timeout = setTimeout(() => {
                setViewport(collectBoundingClientRect(r))
            }, 2)
        }

        window.addEventListener("resize", resizeEventHandler)

        return () => {
            window.removeEventListener("resize", resizeEventHandler)
        }
    }, [r])
    return viewport
}

// scale lavels is an array of snapping points for different scales
// dissecting the first one
// 100000 = break it into hundreds of kilometers, so 1 = 100000 m
// 100 = number of units to go with the number displayed, so 1 = 100 km
// km = unit label
// think of it like:
// `${floor(scale / [0]) * [1]} [2]`
// note: this array needs to be ordered largest to smallest

// returned this way so we can extend it with additional return types down the line
export interface MapSnapshotResult {
    // minX, minY, maxX, maxY
    boundingBox(): [number, number, number, number]
    centerLatLng(): { latitude: number; longitude: number }
    zoom(): number
}

type SnapshotFrameOptions = {
    viewport: WebMercatorViewport
    maxWidth: number
    maxHeight: number
    aspectRatio?: number
    rightMargin?: number
}

export const computeSnapshotFrame = ({
    viewport,
    maxHeight,
    maxWidth,
    aspectRatio = 11 / 8.5,
    rightMargin = 0,
}: SnapshotFrameOptions) => {
    let height = viewport.height * maxHeight
    let width = height * aspectRatio
    if (width > viewport.width * maxWidth) {
        width = viewport.width * maxWidth
        height = width / aspectRatio
    }
    const offsetY = (viewport.height - height) * 0.5
    const offsetX = (viewport.width - rightMargin - width) * 0.5

    return { width, height, offsetX, offsetY }
}

export type MapSnapshotProps = {
    useViewport: () => WebMercatorViewport
    maxWidth: number
    maxHeight: number
    aspectRatio?: number
    hideLayers?: boolean
    onConfirm?: (r: MapSnapshotResult) => void
    onCancel?: () => void
    rightMargin?: number
}

export const MapSnapshot = ({
    useViewport,
    maxWidth,
    maxHeight,
    onConfirm,
    onCancel,
    aspectRatio = 11 / 8.5,
    rightMargin = 0,
}: MapSnapshotProps) => {
    const svgRef = useRef<SVGSVGElement>(null),
        svgViewport = useSVGViewport(svgRef),
        maskId = useHtmlId()

    const viewport = useViewport()

    const { width, height, offsetX, offsetY } = computeSnapshotFrame({
        viewport,
        maxHeight,
        maxWidth,
        aspectRatio,
        rightMargin,
    })

    // scale is meters per pixel

    const { latitude, longitude, zoom } = viewport

    const onConfirmClosure = React.useCallback(() => {
        if (onConfirm) {
            onConfirm({
                boundingBox: () => {
                    const [oMinX, oMinY, oMaxX, oMaxY] = viewport.getBounds(),
                        pWidth = width / viewport.width,
                        pHeight = height / viewport.height,
                        dWidth = oMaxX - oMinX,
                        dHeight = oMaxY - oMinY,
                        oWidth = ((1 - pWidth) * dWidth) / 2,
                        oHeight = ((1 - pHeight) * dHeight) / 2,
                        minX = oMinX + oWidth,
                        maxX = oMaxX - oWidth,
                        minY = oMinY + oHeight,
                        maxY = oMaxY - oHeight
                    return [minX, minY, maxX, maxY]
                },
                centerLatLng: () => ({ latitude, longitude }),
                zoom: () => zoom,
            })
        }
    }, [onConfirm, latitude, longitude, zoom, viewport, height, width])

    return (
        <>
            <svg
                ref={svgRef}
                viewBox={`0 0 ${svgViewport.width} ${svgViewport.height}`}
                width={svgViewport.width}
                height={svgViewport.height}
                className="map-snapshot__shadowbox absolute top-0"
            >
                <mask id={maskId}>
                    <rect
                        x="0"
                        y="0"
                        width={svgViewport.width}
                        height={svgViewport.height}
                        fill="white"
                    />
                    <rect
                        x={offsetX}
                        y={offsetY}
                        width={width}
                        height={height}
                        fill="black"
                        rx="0"
                    />
                </mask>
                <g mask={`url(#${maskId})`}>
                    <rect
                        x="0"
                        y="0"
                        width={svgViewport.width}
                        height={svgViewport.height}
                        fill="#0C0C0F"
                        fillOpacity="0.7"
                    />
                </g>
            </svg>
            {onConfirm && onCancel && (
                <div className="map-snapshot__controls" style={{ top: offsetY + height + 15 }}>
                    <ButtonBar>
                        <ButtonPrimary onClick={onConfirmClosure} title="Confirm" />
                        <ButtonSecondary onClick={onCancel} title="Cancel" />
                    </ButtonBar>
                </div>
            )}
        </>
    )
}
